import { use, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useInterval } from 'usehooks-ts';
import AuthProviderContext from 'shared/features/auth/authentication/providers/contexts/auth-provider-context';
import { isLoggingIn } from 'shared/features/auth/authentication/utils/logging-in-util';
import { useSubscriptionInfoContextProvider } from 'shared/providers/subscription-info/context/subscription-info-provider';
import { useDeleteSubscriptionInfoMutation } from 'shared/providers/subscription-info/domain/reducers/subscription-queries';

import { MessageType } from './domain/models/message-type';
import { Notification } from './domain/models/notification';
import { useLazyOnReconnectQuery } from './domain/queries/notification-queries';
import signalRRetryPolicy from './signalr-retry-policy';

export interface ISignalRHook {
  onNotification: (newMethod: (notification: Notification) => void) => () => void;
}

const notificationMethodName = 'notification';

let notificationHubConnection: HubConnection | null;

const useSignalR = (): ISignalRHook => {
  const { user, isAuthenticated } = useAuth();
  const { logout } = use(AuthProviderContext);
  const { subscriptionInfo, isFinishedExpireNotifications } = useSubscriptionInfoContextProvider();
  const { mutate: executeDeleteSubscriptionInfoMutation } = useDeleteSubscriptionInfoMutation();
  const listenersRef = useRef<((notification: Notification) => void)[]>([]);
  const userRef = useRef(user);
  const { refetch: refetchOnReconnect } = useLazyOnReconnectQuery();

  const IS_RECONNECTING_NOTIFICATION_HUB = 'isReconnectingNotificationHub';
  const LAST_SETUP_CONNECTION_TIME_RETRY = 'lastSetupConnectionTimeRetry';

  const handleNotification = (notification: Notification): void => {
    if (notification.messageType === MessageType.Logout && isLoggingIn() !== 'true' && isAuthenticated) {
      executeDeleteSubscriptionInfoMutation();
      logout();
    }
  };

  const getToken = (): string => userRef.current?.access_token ?? '';

  const setupNotificationHubConnection = (): void => {
    if (notificationHubConnection?.state === HubConnectionState.Connected) {
      return;
    }

    notificationHubConnection = new HubConnectionBuilder()
      .withUrl(`${subscriptionInfo?.connectionString ?? ''}/notification`, {
        accessTokenFactory: getToken,
      })
      .withKeepAliveInterval(15000)
      .withAutomaticReconnect(signalRRetryPolicy)
      .build();

    notificationHubConnection.start().then(() => {
      if (notificationHubConnection?.state === HubConnectionState.Connected) {
        localStorage.removeItem(IS_RECONNECTING_NOTIFICATION_HUB);
        localStorage.removeItem(LAST_SETUP_CONNECTION_TIME_RETRY);
      }
    });

    notificationHubConnection.on(notificationMethodName, (message: string) => {
      const notification: Notification = JSON.parse(message);
      listenersRef.current.forEach((listener) => listener(notification));
    });

    notificationHubConnection.onreconnected(() => {
      refetchOnReconnect();
      localStorage.removeItem(IS_RECONNECTING_NOTIFICATION_HUB);
      localStorage.removeItem(LAST_SETUP_CONNECTION_TIME_RETRY);
    });

    notificationHubConnection.onreconnecting(() => {
      localStorage.setItem(IS_RECONNECTING_NOTIFICATION_HUB, 'true');
      localStorage.setItem(LAST_SETUP_CONNECTION_TIME_RETRY, Date.now().toString());
    });
  };

  const destroyNotificationHubConnection = (): void => {
    if (!notificationHubConnection) {
      return;
    }

    notificationHubConnection.off(notificationMethodName);
    notificationHubConnection.stop();
    localStorage.removeItem(IS_RECONNECTING_NOTIFICATION_HUB);
    localStorage.removeItem(LAST_SETUP_CONNECTION_TIME_RETRY);
    notificationHubConnection = null;
  };

  const onNotification = (newMethod: (notification: Notification) => void): (() => void) => {
    listenersRef.current = [...listenersRef.current, newMethod];

    return () => {
      listenersRef.current = listenersRef.current.filter((listener) => listener !== newMethod);
    };
  };

  const retrySignalRConnections = (): void => {
    const retryTimeOut = 10000;
    const now = Date.now();
    const lastConnectionRetry = Number(localStorage.getItem(LAST_SETUP_CONNECTION_TIME_RETRY));

    if (now - lastConnectionRetry >= retryTimeOut) {
      localStorage.setItem(LAST_SETUP_CONNECTION_TIME_RETRY, now.toString());
      setupNotificationHubConnection();
    }
  };

  useInterval(() => retrySignalRConnections(), localStorage.getItem(IS_RECONNECTING_NOTIFICATION_HUB) === 'true' ? 1000 : null);

  useEffect(() => {
    const offNotification = onNotification(handleNotification);

    return (): void => {
      offNotification();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (subscriptionInfo && isFinishedExpireNotifications) {
      setupNotificationHubConnection();
    } else {
      destroyNotificationHubConnection();
    }

    return (): void => {
      destroyNotificationHubConnection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionInfo, isFinishedExpireNotifications]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  return {
    onNotification,
  };
};

export default useSignalR;
